import React from 'react';
import { Navigate, useLoaderData, useParams } from 'react-router-dom';
import { Await } from '@oysterjs/ui/async';

import type { EmailTemplateEditorFieldsFragment } from '~/api/graphql';
import { LibraryListLoading } from '~/components/list/library-list-loading';
import {
  EmailTemplateEditor,
  EmailTemplatePageTitle
} from '~/components/email-templates/email-template-editor';
import { EMAIL_TEMPLATES_PATH } from '~/components/email-templates/email-template-paths';
import { LibraryListLoadError } from '~/components/list/library-list-load-error';

type EmailTemplateEditorLoaderData = {
  data: Promise<{ emailTemplate: EmailTemplateEditorFieldsFragment | null }>;
};

const useEditorTitle = () => (useParams().templateId ? 'Edit template' : 'New template');

export const EmailTemplateEditorPage = () => {
  const loaderData = useLoaderData() as EmailTemplateEditorLoaderData;
  const isNewTemplate = !useParams().templateId;
  const title = useEditorTitle();
  if (isNewTemplate) return <EmailTemplateEditor template={null} />;

  return (
    <React.Suspense
      fallback={
        <EmailTemplatePageTitle title={title}>
          <LibraryListLoading title="Loading template…" />
        </EmailTemplatePageTitle>
      }
    >
      <Await resolve={loaderData.data}>
        {({ emailTemplate }) => {
          if (!emailTemplate) return <Navigate to={EMAIL_TEMPLATES_PATH} replace />;
          return <EmailTemplateEditor key={emailTemplate.id} template={emailTemplate} />;
        }}
      </Await>
    </React.Suspense>
  );
};

export const EmailTemplateEditorLoadError = () => (
  <EmailTemplatePageTitle title={useEditorTitle()}>
    <LibraryListLoadError title="We couldn't load this template." />
  </EmailTemplatePageTitle>
);
